/**
 * Compact list-view row for the V2 Properties page.
 *
 * Used as an alternative to the card grid when editors want a denser,
 * scannable view that surfaces more metadata at once. Built as a row of
 * Cards (instead of an HTML <table>) so it lays out cleanly on mobile
 * and keeps the same shadow/hover language as the rest of the V2 admin.
 */
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  EyeOff,
  MailWarning,
  MapPin,
  MapPinOff,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { PropertyListItem } from "@/types/property";
import { useLanguage } from "@/contexts/LanguageContext";
import { hasPlottableCoordinates } from "@/lib/coordinates";
import { usePropertyReminderStats } from "@/hooks/v2/usePropertyReminderStats";

type Props = {
  property: PropertyListItem;
  /** Optional secondary line to show under title — e.g. "Lehel GmbH" lessor name. */
  lessorName?: string | null;
};

export function PropertyListRow({ property: p, lessorName }: Props) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const occupancyPct =
    p.rooms_count > 0
      ? Math.round((p.occupied_count / p.rooms_count) * 100)
      : null;

  // Reminders badge: current-period overdue rows + open past-month rows
  // (COL-435), from the shared hook so this count matches the property's
  // Reminders tab and the other property cards.
  const { data: reminderStats, isError: reminderStatsError } =
    usePropertyReminderStats(p.id, {
      propertyRemindersEnabled: p.rent_reminders_enabled,
    });
  const overdueCount = reminderStats?.total ?? 0;
  const inkassoCount = reminderStats?.inkasso ?? 0;
  const hasReminders = overdueCount > 0;

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/admin/properties/${p.id}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ")
          navigate(`/admin/properties/${p.id}`);
      }}
      className="block group focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-lg cursor-pointer"
    >
      <Card className="p-3 group-hover:shadow-sm transition-shadow flex items-center gap-4">
        {/* Thumbnail */}
        <div className="h-16 w-24 rounded-md overflow-hidden bg-muted shrink-0 relative">
          {p.cover_image ? (
            <img
              src={p.cover_image}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <Building2 className="h-6 w-6 text-muted-foreground/40" />
            </div>
          )}
          {p.is_hidden && (
            <span className="absolute top-1 left-1 bg-background/90 rounded px-1 py-0.5 text-[10px] font-medium flex items-center gap-0.5">
              <EyeOff className="h-2.5 w-2.5" /> Hidden
            </span>
          )}
        </div>

        {/* Title + city + lessor */}
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium leading-tight truncate">{p.title}</h3>
            {p.type && (
              <Badge variant="outline" className="font-normal text-xs">
                {p.type}
              </Badge>
            )}
            {/* Same signal as the grid card: an ungeocoded property shows no
                map on its public room pages, and nothing else says so. */}
            {!hasPlottableCoordinates(p.lat, p.lng) && (
              <Badge
                variant="outline"
                className="gap-1 border-amber-200 bg-amber-50 font-normal text-xs text-amber-800"
                title={t(
                  "admin.property.badge.noLocationHint",
                  "No coordinates — the public room pages show no map. Re-pick the address in Edit to add one."
                )}
              >
                <MapPinOff className="h-3 w-3" />
                {t("admin.property.badge.noLocation", "No location")}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1 truncate">
              <MapPin className="h-3 w-3 shrink-0" />
              {p.city}
            </span>
            {lessorName && (
              <>
                <span className="opacity-60">·</span>
                <span className="truncate">{lessorName}</span>
              </>
            )}
          </div>
        </div>

        {/* Stats: rooms · occupancy */}
        <div className="hidden sm:flex items-center gap-6 shrink-0 text-sm">
          <Stat
            value={p.rooms_count}
            label={p.rooms_count === 1 ? "room" : "rooms"}
            muted={p.rooms_count === 0}
          />
          <Stat
            value={occupancyPct === null ? "—" : `${occupancyPct}%`}
            label={
              occupancyPct === null
                ? "no rooms"
                : `${p.occupied_count}/${p.rooms_count} occupied`
            }
            muted={occupancyPct === null}
          />
          <Link
            to={`/admin/properties/${p.id}?tab=reminders`}
            onClick={(e) => e.stopPropagation()}
            className={
              "inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium border transition-colors " +
              (hasReminders
                ? inkassoCount > 0
                  ? "bg-rose-600 text-white border-rose-600/30 hover:bg-rose-700"
                  : "bg-rose-600 text-white border-rose-600/30 hover:bg-rose-700"
                : "bg-transparent text-muted-foreground border-border/60 hover:bg-muted/60 hover:text-foreground")
            }
            title={
              reminderStatsError
                ? "Could not load reminders"
                : hasReminders
                  ? `${overdueCount} overdue`
                  : "Reminders"
            }
            aria-label="Open reminders for this property"
          >
            <MailWarning className="h-3.5 w-3.5" />
            {reminderStatsError ? (
              <span className="tabular-nums font-bold">—</span>
            ) : hasReminders ? (
              <span className="tabular-nums font-bold">
                {overdueCount > 99 ? "99+" : overdueCount}
              </span>
            ) : null}
          </Link>
        </div>

        {/* Mobile: condensed badge stack */}
        <div className="sm:hidden flex flex-col items-end gap-1 shrink-0">
          <Badge variant="outline" className="text-xs font-normal">
            {p.rooms_count} {p.rooms_count === 1 ? "room" : "rooms"}
          </Badge>
          {occupancyPct !== null && (
            <Badge
              variant={occupancyPct === 100 ? "default" : "secondary"}
              className="text-xs font-normal"
            >
              {occupancyPct}%
            </Badge>
          )}
        </div>

        <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 hidden sm:block" />
      </Card>
    </div>
  );
}

function Stat({
  value,
  label,
  muted,
}: {
  value: React.ReactNode;
  label: string;
  muted?: boolean;
}) {
  return (
    <div className="text-right min-w-[70px]">
      <div
        className={
          muted
            ? "text-sm font-medium tabular-nums text-muted-foreground"
            : "text-sm font-semibold tabular-nums"
        }
      >
        {value}
      </div>
      <div className="text-[11px] text-muted-foreground leading-tight">
        {label}
      </div>
    </div>
  );
}
